import { createContext, useContext } from 'react';

import type { SourceImageRef } from '../../../domain';

/**
 * Auto-bake CSS filters into a fresh asset right before an i2i op submits
 * (Spike 2 Slice C — plan §2.4). The CSS filter sliders are non-destructive
 * — they render the canvas via `previewFilterCss`, but the underlying
 * `images[activeIndex]` asset is unfiltered. Without intervention every
 * i2i op ships the *unfiltered* image to the BE, and the user's "I tuned
 * my image" intent is silently lost.
 *
 * The Image Lab shell provides `bakeIfNeeded`. When called, it:
 *   1. Checks the live `FiltersState`. If neutral, returns `null` — caller
 *      proceeds with the original source.
 *   2. Loads the active image's HTMLImageElement, runs `bakeFiltersToBlob`,
 *      uploads via the standard asset pipeline.
 *   3. Appends the baked asset to `images`, advances `activeIndex`, resets
 *      filter sliders to neutral, and surfaces a toast.
 *   4. Returns the baked asset's `SourceImageRef` so the caller can splice
 *      it into the op request.
 *
 * `useOperationMutation` reads this context and applies the bake before
 * sending so panels need no per-op plumbing. The two exceptions:
 *
 *   - TryOnRequest's canvas image is `modelImage*`, not `sourceImage*`.
 *     TryOnPanel passes `canvasSourceField: 'modelImage'` to retarget the
 *     bake's effect onto the right field group.
 *   - SketchToImage's source is the sketch tablet dataURL, not a canvas
 *     image with filters. SketchToImagePanel opts out by passing
 *     `canvasSourceField: null`.
 *
 * Default value (no-op) keeps tests and headless usages safe — operations
 * run unchanged outside an Image Lab shell.
 */
export interface BakeOnSubmitValue {
  /** Returns the new SourceImageRef if a bake occurred, else null. Throws
   *  if the bake-or-upload pipeline fails — the op should fail with it so
   *  the user isn't silently downgraded to the unfiltered source. */
  bakeIfNeeded: () => Promise<SourceImageRef | null>;
}

const NOOP_VALUE: BakeOnSubmitValue = {
  bakeIfNeeded: async () => null,
};

export const BakeOnSubmitContext = createContext<BakeOnSubmitValue>(NOOP_VALUE);

export function useBakeOnSubmit(): BakeOnSubmitValue {
  return useContext(BakeOnSubmitContext);
}
